import { format } from 'date-fns'
import { BusinessSiteSuggestion, TRoute, TRoutesInput } from 'gql/generated/graphql'
import { GTRouteSuggestion } from 'gql/types.generated'
import { sanitizeDate, US_DATE_FORMAT } from 'utils/date'

export interface IRoutesPlanningSearchQuery {
  businessClientId: string
  companyId: string
  businessSiteId: string
  routesIds: string
  offerDateStartAt: string
  offerDateEndAt: string
}

export interface IRoutesPlanningFilters {
  businessSite: {
    id: string
    name: string
  }
  routes: {
    id: string
    name: string
  }[]
  offerDateRange: {
    startAt: Date | null
    endAt: Date | null
  }
}

export interface IRoutesPlanningFiltersModel extends IRoutesPlanningFilters {
  toQueryInput: () => TRoutesInput
  hasFiltersApplied: () => boolean
}

export const createRoutesPlanningFiltersModel = (
  searchQuery: IRoutesPlanningSearchQuery,
  {
    businessSiteAutocomplete,
    routesAutocomplete
  }: {
    businessSiteAutocomplete: BusinessSiteSuggestion | null
    routesAutocomplete: GTRouteSuggestion[]
  }
): IRoutesPlanningFiltersModel => {
  const offerDateStartAt = sanitizeDate(searchQuery.offerDateStartAt)
  const offerDateEndAt = sanitizeDate(searchQuery.offerDateEndAt)

  return {
    businessSite: {
      id: businessSiteAutocomplete?.id || '',
      name: businessSiteAutocomplete?.name || ''
    },
    routes: routesAutocomplete.map((routeAutocomplete) => ({
      id: routeAutocomplete.id,
      name: routeAutocomplete.name
    })),
    offerDateRange: {
      startAt: sanitizeDate(searchQuery.offerDateStartAt) || null,
      endAt: sanitizeDate(searchQuery.offerDateEndAt) || null
    },
    toQueryInput: (): TRoutesInput => {
      return {
        companyID: searchQuery.companyId || null,
        businessClientID: searchQuery.businessClientId || null,
        businessSiteID: searchQuery.businessSiteId || null,
        ids: searchQuery.routesIds ? searchQuery.routesIds.split(',') : undefined,
        offerDateRange:
          offerDateStartAt || offerDateEndAt
            ? {
                startAt: offerDateStartAt ? format(offerDateStartAt, US_DATE_FORMAT) : null,
                endAt: offerDateEndAt ? format(offerDateEndAt, US_DATE_FORMAT) : null
              }
            : null
      }
    },
    hasFiltersApplied: (): boolean => {
      return Boolean(
        searchQuery.businessSiteId ||
          searchQuery.routesIds ||
          searchQuery.offerDateStartAt ||
          searchQuery.offerDateEndAt
      )
    }
  }
}

export interface IRoutePlanningModel extends TRoute {
  rgbColor: string
  isVisible: boolean
  polyline: TRoute['polyline'] & {
    decodedPath: google.maps.LatLngLiteral[]
  }
}

export const createRoutePlanningModel = (
  route: TRoute,
  rgbColor: string,
  isVisible: boolean,
  geometryLib: google.maps.GeometryLibrary | null
): IRoutePlanningModel => {
  return {
    ...route,
    name: route.name || `${route.stops.at(0)?.name} - ${route.stops.at(-1)?.name}`,
    polyline: {
      distance: route.polyline?.distance || 0,
      encoded: route.polyline?.encoded || '',
      decodedPath: geometryLib
        ? geometryLib.encoding.decodePath(route.polyline?.encoded || '').map((point) => ({
            lat: point.lat(),
            lng: point.lng()
          }))
        : []
    },
    rgbColor,
    isVisible
  }
}
